import React, { useState, useEffect } from 'react';
import './DashboardConversationsBlock.css';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const timeOptions = ['Today', 'This Week', 'This Month', 'This Year', 'All Time'];
const groupByOptions = [
  { value: 'category', label: 'Category' },
  { value: 'status', label: 'Status' },
  { value: 'closedByName', label: 'Closed by User' },
  { value: 'createdBy', label: 'Created by' },
];

const DashboardConversationsBlock = ({ currentUser }) => {
  const [data, setData] = useState({ summary: {}, conversations: [] });
  const [showChart, setShowChart] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState('This Month');
  const [groupBy, setGroupBy] = useState('category');

  useEffect(() => {
    const fetchData = async () => {
      const response = await axios.post(
        'https://gambot.azurewebsites.net/api/Webhooks/GetConversationsDashboardStats',
        { organization: currentUser.organization }
      );
      setData(response.data?.Data || { summary: {}, conversations: [] });
    };

    if (currentUser?.organization) fetchData();
  }, [currentUser]);

  const filterConversationsByTime = (range) => {
    const now = new Date();
    if (range === 'All Time') return data.conversations;

    return data.conversations.filter((conv) => {
      const created = new Date(conv.createdOn);
      const diffHours = (now - created) / (1000 * 60 * 60);

      switch (range) {
        case 'Today':
          return created.toDateString() === now.toDateString();
        case 'This Week':
          return diffHours < 168;
        case 'This Month':
          return now.getMonth() === created.getMonth() && now.getFullYear() === created.getFullYear();
        case 'This Year':
          return now.getFullYear() === created.getFullYear();
        default:
          return true;
      }
    });
  };

  const filteredConversations = filterConversationsByTime(selectedTimeRange);

  const countByField = (fieldName) => {
    const count = {};
    filteredConversations.forEach((conv) => {
      const value = conv[fieldName] || 'Unknown';
      count[value] = (count[value] || 0) + 1;
    });
    return count;
  };

  const counts = countByField(groupBy);

  const chartData = {
    labels: Object.keys(counts),
    datasets: [
      {
        label: `${groupByOptions.find(opt => opt.value === groupBy)?.label || ''}`,
        data: Object.values(counts),
        backgroundColor: '#607D8B',
        borderRadius: 8,
      },
    ],
  };

  const { total = 0, open = 0, closed = 0 } = data.summary;

  return (
    <div className="dashboard-conversations-block">
      <div className="dashboard-conversations-block-header-row">
        <h2 className="dashboard-conversations-block-title">Conversations</h2>
        <button className="dashboard-conversations-block-toggle" onClick={() => setShowChart(!showChart)}>
          {showChart ? 'Show Table' : 'Show Chart'}
        </button>
      </div>

      {showChart ? (
        <>
          <div className="dashboard-conversations-block-controls">
            <select value={selectedTimeRange} onChange={(e) => setSelectedTimeRange(e.target.value)}>
              {timeOptions.map((opt) => (
                <option key={opt}>{opt}</option>
              ))}
            </select>

            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              {groupByOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="dashboard-conversations-block-chart">
            <Bar
              data={chartData}
              options={{
                responsive: true,
                plugins: { legend: { display: true } },
              }}
            />
          </div>
        </>
      ) : (
        <div className="dashboard-conversations-block-grid">
          <div className="dashboard-conversations-block-label"></div>
          <div className="dashboard-conversations-block-header">Total</div>
          <div className="dashboard-conversations-block-header">Open</div>
          <div className="dashboard-conversations-block-header">Closed</div>

          <div className="dashboard-conversations-block-label">Count</div>
          <div className="dashboard-conversations-block-cell">
            <div className="dashboard-conversations-block-circle">{total}</div>
          </div>
          <div className="dashboard-conversations-block-cell">
            <div className="dashboard-conversations-block-circle">{open}</div>
          </div>
          <div className="dashboard-conversations-block-cell">
            <div className="dashboard-conversations-block-circle">{closed}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardConversationsBlock;
